/**
 * event-cover-upload — the event cover goes to the canvas asset store and the event keeps its
 * https address (CC 0395).
 *
 * An inline `data:` cover is dropped by the public renderer (Open Graph needs an absolute https
 * image) and by the page's image block (it accepts only asset-store images), so an event made in
 * Studio never had a page or link-preview image. The cover now takes the template assets' path:
 * presign (visibility public) → PUT the bytes to the signed URL → confirm, and the confirmed
 * asset's https download URL is what the event stores. Nothing is stored until all three succeed.
 */
import { canvas } from "@v0y4ge/api-client";
import { t as i18nT } from "./i18n";

export type CoverStage = "presigning" | "uploading" | "confirming";
const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];

function imageSize(file: File): Promise<{ width: number; height: number } | null> {
  if (typeof URL === "undefined" || typeof Image === "undefined" || !URL.createObjectURL) return Promise.resolve(null);
  return new Promise((resolve) => {
    const src = URL.createObjectURL(file);
    const img = new Image();
    const done = (v: { width: number; height: number } | null) => { URL.revokeObjectURL(src); resolve(v); };
    img.onload = () => done(img.naturalWidth && img.naturalHeight ? { width: img.naturalWidth, height: img.naturalHeight } : null);
    img.onerror = () => done(null);
    img.src = src;
  });
}

/** An absolute https asset address the public page can use, or "". */
function httpsAssetUrl(confirmed: Record<string, unknown>): string {
  const raw = String(confirmed.download_url ?? confirmed.downloadUrl ?? "");
  return /^https:\/\//i.test(raw) ? raw : "";
}

/** Upload one cover image and return its https asset URL. Throws with a message the field can show;
 *  the caller keeps the previous image on any failure. */
export async function uploadEventCover(file: File, opts: { onStage?: (s: CoverStage) => void; signal?: AbortSignal } = {}): Promise<string> {
  if (!ACCEPTED.includes(file.type)) throw new Error(i18nT("journeys.cover.type", "Use a JPG, PNG or WebP image."));
  opts.onStage?.("presigning");
  const size = await imageSize(file);
  let presigned: Record<string, unknown>;
  try {
    presigned = (await canvas.presignCanvasAsset({
      asset_kind: "image", filename: file.name || "event-cover", mime_type: file.type, byte_size: file.size,
      visibility: "public", tags: ["event-cover"], ...(size ? { width: size.width, height: size.height } : {}),
    })) as unknown as Record<string, unknown>;
  } catch {
    throw new Error(i18nT("journeys.cover.presignFailed", "The upload couldn't be started. Your previous image is kept. Try again in a moment."));
  }
  const assetId = String(presigned.id ?? "");
  const uploadUrl = String(presigned.upload_url ?? "");
  const maxBytes = Number(presigned.max_bytes);
  if (!assetId || !uploadUrl) throw new Error(i18nT("journeys.cover.presignFailed", "The upload couldn't be started. Your previous image is kept. Try again in a moment."));
  if (Number.isFinite(maxBytes) && maxBytes > 0 && file.size > maxBytes) {
    throw new Error(i18nT("journeys.cover.tooLarge", "This image is larger than {size} MB. Choose a smaller one.").replace("{size}", (maxBytes / 1048576).toFixed(1)));
  }
  if (opts.signal?.aborted) throw new Error("aborted");
  opts.onStage?.("uploading");
  try {
    await canvas.uploadCanvasAssetBytes(uploadUrl, file, { signal: opts.signal });
  } catch {
    throw new Error(i18nT("journeys.cover.uploadFailed", "The image upload was rejected. Your previous image is kept. Try again, or choose a different image."));
  }
  if (opts.signal?.aborted) throw new Error("aborted");
  opts.onStage?.("confirming");
  let confirmed: Record<string, unknown>;
  try {
    confirmed = (await canvas.confirmCanvasAsset(assetId, size ? { width: size.width, height: size.height } : {})) as unknown as Record<string, unknown>;
  } catch {
    throw new Error(i18nT("journeys.cover.confirmFailed", "The image uploaded but couldn't be confirmed. Your previous image is kept. Try again in a moment."));
  }
  const url = httpsAssetUrl(confirmed);
  if (!url) throw new Error(i18nT("journeys.cover.noUrl", "The image was stored without a public https address, so it can't be the event's image. Your previous image is kept."));
  return url;
}

/** One cover upload at a time, with a token (CC 0426): a new upload, a dropped address or Remove cancels the
 *  one in flight, so its answer is ignored (an asset it confirmed is just left in the store). `busy()` is what
 *  Save, Save draft and Publish wait on, so an event is never created or saved without the cover being chosen. */
export function createCoverUploader(upload: typeof uploadEventCover = uploadEventCover) {
  let token = 0;
  let busy = false;
  return {
    busy: () => busy,
    cancel() { token += 1; busy = false; },
    start(file: File, h: { onStage: (s: CoverStage) => void; onDone: (url: string) => void; onError: (message: string) => void }) {
      const run = ++token;
      busy = true;
      h.onStage("presigning");
      upload(file, { onStage: (s) => { if (run === token) h.onStage(s); } }).then(
        (url) => { if (run !== token) return; busy = false; h.onDone(url); },
        (e) => { if (run !== token) return; busy = false; h.onError(e instanceof Error ? e.message : String(e)); },
      );
    },
  };
}
